import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';

/**
 * CustomConfigService
 *
 * This service extends the shared ConfigService to allow project-specific overrides.
 * It is pre-built and placed in the base project so developers can modify it if needed.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomConfigService extends ConfigService {
  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Override unwrapResponse so that list/table responses (responseList[0].data with
   * multiple items) are returned as-is, letting extractRows navigate via rowsPath.
   * Single-item responses keep the original behaviour (returning data[0]).
   */
  protected override unwrapResponse(response: any): any {
    if (response?.responseList?.length > 0) {
      const firstItem = response.responseList[0];
      if (firstItem?.data?.length > 0) {
        if (firstItem.data.length > 1) {
          // Multi-row response: return full response so rowsPath can navigate correctly
          return response;
        }
        return firstItem.data[0];
      }
    }
    return response;
  }

  /**
   * Force flat payload format for all chart data requests.
   * The /spriced/platform/ API expects: { key: value, ... }
   */
  override getConfig(
    url: string,
    data?: any,
    filters?: any,
    method?: string,
    isTable = false,
  ): Observable<any> {
    return super.getConfig(
      url,
      data ? { ...data, skipDefaultPayload: true } : data,
      filters,
      method,
      isTable,
    );
  }

  /**
   * Normalize payload before sending dropdown options requests.
   * Strips any envelope wrapper ({requestData, responseList, endpoints...})
   * and flattens to a simple key-value object.
   */
  override getDropdownOptions(
    url: string,
    payload: any,
    method = 'post',
    headers?: Record<string, string>,
  ): Observable<any> {
    return super.getDropdownOptions(url, this.flattenEnvelope(payload), method, headers);
  }

  private flattenEnvelope(payload: any): any {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
    if (!('responseList' in payload) && !('requestData' in payload)) return payload;
    const { requestData = {}, responseList, entity, workflowId, endpoints, communicationType, version, ...rest } = payload;
    return { ...requestData, ...rest };
  }

  /**
   * Pass-through for getApi if needed, or override similarly
   */
  override getApi(
    url: string,
    filters?: any,
    data?: any,
    method = 'post',
  ): Observable<any> {
    console.groupCollapsed(
      '%c[CustomConfigService] getApi called',
    );
    // ---------------------------------------------------------
    // CUSTOM PAYLOAD OVERRIDE EXAMPLE
    // If you want a COMPLETELY different payload structure (bypassing the Standard Base Payload),
    // intercept it here using the widgetId.
    //
    // Trigger this by setting "customPayload": true in your widget config.
    // ---------------------------------------------------------
    if (data?.customPayload){
        if (data?.widgetId === 'widget2') {
        const myUniquePayload = {
            // DEFINE YOUR CUSTOM PAYLOAD HERE
            key: 'value',
            specialData: true
        };
        return this.http.post(url, myUniquePayload);
    }

    }

    // ---------------------------------------------------------

    return super.getApi(url, filters, data);
  }
}
